import { useEffect, useState } from 'react'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  Users,
  Truck,
  DollarSign,
  AlertTriangle,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react'
import StatsCard from '../components/StatsCard'
import FunnelCard from '../components/FunnelCard'
import { fetchAdminStats, fetchPickups, fetchWasteDistribution } from '../services/adminService'
import type { AdminStats, Pickup } from '../types'

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  SCHEDULED: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  MISSED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
}

const WASTE_COLORS: Record<string, string> = {
  GENERAL: 'text-gray-600 bg-gray-100',
  RECYCLABLE: 'text-green-700 bg-green-100',
  ORGANIC: 'text-orange-700 bg-orange-100',
  EWASTE: 'text-purple-700 bg-purple-100',
  BULK: 'text-blue-700 bg-blue-100',
}

const WASTE_LABELS: Record<string, string> = {
  GENERAL: 'General',
  RECYCLABLE: 'Recyclable',
  ORGANIC: 'Organic',
  EWASTE: 'E-Waste',
  BULK: 'Bulk',
}

const WASTE_PIE_COLORS = ['#6B7280', '#4CAF50', '#FF6B35', '#8B5CF6', '#3B82F6']

interface WasteDistItem { name: string; value: number; color: string }

export default function Dashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [recentPickups, setRecentPickups] = useState<Pickup[]>([])
  const [wasteDistribution, setWasteDistribution] = useState<WasteDistItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    async function load() {
      try {
        const [statsData, pickupsData, wasteData] = await Promise.all([
          fetchAdminStats(),
          fetchPickups({ page: 1, limit: 6 }),
          fetchWasteDistribution(),
        ])
        setStats(statsData)
        setRecentPickups(pickupsData?.pickups ?? pickupsData ?? [])
        const dist = wasteData?.distribution ?? []
        setWasteDistribution(
          dist.map((d: { wasteType: string; percentage: number }) => ({
            name: WASTE_LABELS[d.wasteType] ?? d.wasteType,
            value: d.percentage,
            color: WASTE_PIE_COLORS[Object.keys(WASTE_LABELS).indexOf(d.wasteType)] ?? '#6B7280',
          }))
        )
      } catch (err: unknown) {
        const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to load dashboard data'
        setError(message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm">
        {error}
      </div>
    )
  }

  const s = stats!

  // Build pickups by status from real stats
  const pickupsByStatus = [
    { status: 'Completed', count: s.completedPickups, color: '#2D7D46' },
    { status: 'Pending', count: s.pendingPickups, color: '#FF6B35' },
    { status: 'Missed', count: s.missedPickups, color: '#EF4444' },
  ]

  const revenueFormatted = `GHS ${s.revenueThisMonth?.toLocaleString() ?? 0}`
  const revenueGrowth = s.revenueGrowth != null ? `${s.revenueGrowth > 0 ? '+' : ''}${s.revenueGrowth.toFixed(1)}%` : ''

  const subRevenue = s.subscriptionRevenue ?? 0
  const pickupRevenue = s.pickupRevenue ?? 0

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatsCard
          title="Total Users"
          value={s.totalUsers?.toLocaleString() ?? '0'}
          icon={Users}
          trend="up"
          trendValue={`${s.newUsersThisWeek ?? 0} new this week`}
          color="blue"
          subtitle={`${s.totalResidents ?? 0} residents · ${s.totalCollectors ?? 0} collectors`}
        />
        <StatsCard
          title="Total Pickups"
          value={s.totalPickups?.toLocaleString() ?? '0'}
          icon={Truck}
          trend="up"
          trendValue={`${s.completedPickups ?? 0} completed`}
          color="green"
          subtitle={`${s.pendingPickups ?? 0} pending · ${s.missedPickups ?? 0} missed`}
        />
        <StatsCard
          title="Revenue This Month"
          value={revenueFormatted}
          icon={DollarSign}
          trend={s.revenueGrowth >= 0 ? 'up' : 'down'}
          trendValue={revenueGrowth}
          color="orange"
          subtitle={`Last month: GHS ${s.revenueLastMonth?.toLocaleString() ?? 0}`}
        />
        <StatsCard
          title="Open Reports"
          value={String(s.openReports ?? 0)}
          icon={AlertTriangle}
          trend={s.openReports > 0 ? 'up' : 'neutral'}
          trendValue={`${s.inReviewReports ?? 0} in review`}
          color="red"
          subtitle={`${s.resolvedReports ?? 0} resolved`}
        />
      </div>

      {/* Resident funnel — signups to collections, with the biggest loss named.
          Full width and above revenue: with no completed pickups yet, this is
          the tile that says why the revenue tiles read zero. */}
      <FunnelCard />

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs text-[#6B7280] font-medium">Subscription Revenue</p>
          <p className="text-xl font-bold text-blue-700 mt-1">GHS {subRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs text-[#6B7280] font-medium">Pay-Per-Pickup Revenue</p>
          <p className="text-xl font-bold text-green-700 mt-1">GHS {pickupRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs text-[#6B7280] font-medium">Pay-Per-Pickup Count</p>
          <p className="text-xl font-bold text-orange-700 mt-1">{s.payPerPickupCount ?? 0}</p>
          <p className="text-[10px] text-[#6B7280] mt-0.5">{s.activeSubscriptions ?? 0} active subscriptions</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs text-[#6B7280] font-medium">Subscriber Discounts Given</p>
          <p className="text-xl font-bold text-purple-700 mt-1">GHS {(s.totalDiscountSavings ?? 0).toLocaleString()}</p>
          <p className="text-[10px] text-[#6B7280] mt-0.5">{s.discountedPickupCount ?? 0} discounted pickups</p>
        </div>
      </div>

      {/* Subscriber Pickup Usage Monitoring */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="mb-3">
          <h3 className="font-semibold text-[#1A1A2E]">Subscriber Pickup Usage</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">How subscribers are using their 3 included pickups this period</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-gray-500">{s.subscriberUsageDistribution?.used0 ?? 0}</p>
            <p className="text-xs text-[#6B7280] mt-1">0 pickups used</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{s.subscriberUsageDistribution?.used1 ?? 0}</p>
            <p className="text-xs text-[#6B7280] mt-1">1 pickup used</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{s.subscriberUsageDistribution?.used2Plus ?? 0}</p>
            <p className="text-xs text-[#6B7280] mt-1">2+ pickups used</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Waste Distribution */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="mb-4">
            <h3 className="font-semibold text-[#1A1A2E]">Waste Distribution</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">By type</p>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie
                data={wasteDistribution}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={75}
                paddingAngle={3}
                dataKey="value"
              >
                {wasteDistribution.map((_, index) => (
                  <Cell key={index} fill={WASTE_PIE_COLORS[index % WASTE_PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [`${value}%`, '']}
                contentStyle={{ borderRadius: '10px', fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
            {wasteDistribution.map((item) => (
              <div key={item.name} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-xs text-[#6B7280] truncate">{item.name}</span>
                <span className="text-xs font-semibold text-[#1A1A2E] ml-auto">{item.value}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pickups by Status */}
        <div className="xl:col-span-2 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="mb-4">
            <h3 className="font-semibold text-[#1A1A2E]">Pickups by Status</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">Current breakdown</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={pickupsByStatus}
              layout="vertical"
              margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} />
              <YAxis
                dataKey="status"
                type="category"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={false}
                axisLine={false}
                width={70}
              />
              <Tooltip contentStyle={{ borderRadius: '10px', fontSize: '12px' }} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {pickupsByStatus.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Recent Pickups Table */}
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <h3 className="font-semibold text-[#1A1A2E]">Recent Pickups</h3>
              <p className="text-xs text-[#6B7280] mt-0.5">Latest pickup requests</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['ID', 'Resident', 'Type', 'Status', 'Date'].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentPickups.map((p) => {
                  const st = STATUS_STYLES[p.status] ?? STATUS_STYLES.CANCELLED
                  const wasteLabel = WASTE_LABELS[p.wasteType] ?? p.wasteType
                  const wasteStyle = WASTE_COLORS[p.wasteType] ?? 'bg-gray-100 text-gray-600'
                  return (
                    <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono text-[#6B7280]">{p.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-xs font-medium text-[#1A1A2E] whitespace-nowrap">
                        {p.resident?.name ?? p.userName ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${wasteStyle}`}>
                          {wasteLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${st.bg} ${st.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {p.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(p.scheduledDate).toLocaleDateString()}
                      </td>
                    </tr>
                  )
                })}
                {recentPickups.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No pickups found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-[#1A1A2E] mb-4">Overview</h3>
            <div className="space-y-3">
              {[
                { label: 'Pending Pickups', value: s.pendingPickups, icon: Clock, color: 'text-blue-500 bg-blue-50' },
                { label: 'Completed', value: s.completedPickups, icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
                { label: 'Missed', value: s.missedPickups, icon: XCircle, color: 'text-red-500 bg-red-50' },
                { label: 'Open Reports', value: s.openReports ?? 0, icon: AlertTriangle, color: 'text-orange-500 bg-orange-50' },
                { label: 'Total Collectors', value: s.totalCollectors, icon: Users, color: 'text-purple-500 bg-purple-50' },
                { label: 'Pending Approvals', value: s.pendingCollectors ?? 0, icon: Clock, color: 'text-yellow-500 bg-yellow-50' },
                { label: 'Avg. Rating', value: s.averageRating?.toFixed(1) ?? '—', icon: MapPin, color: 'text-teal-500 bg-teal-50' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${color.split(' ')[1]} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${color.split(' ')[0]}`} />
                  </div>
                  <span className="text-xs text-[#6B7280] flex-1">{label}</span>
                  <span className="text-sm font-bold text-[#1A1A2E]">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
